import { useEffect, useRef } from "react";
import "./i18n";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, landingFor, NEXT_KEY, useAuth } from "./lib/auth";
import { api } from "./lib/api";
import i18n from "./i18n";
import Layout from "./components/Layout";
import { Loading } from "./components/common";
import { SignIn, SignUp } from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Profile from "./pages/Profile";
import Home from "./pages/Home";
import Practice from "./pages/Practice";
import DogNew from "./pages/DogNew";
import DogDetail from "./pages/DogDetail";
import ProgrammeDetail from "./pages/ProgrammeDetail";
import VisitPage from "./pages/VisitPage";
import Library, { ProtocolDetail } from "./pages/Library";
import Invite from "./pages/Invite";
import { OwnerHome, OwnerDog, OwnerTask } from "./pages/Owner";
import TaskReview from "./pages/TaskReview";
import Workspace from "./pages/Workspace";
import AuditLog from "./pages/AuditLog";
import Landing from "./pages/Landing";
import DemoStart, { DemoModules } from "./pages/DemoStart";

function AuthCallback() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    api.post("/auth/google", { session_id: sid, interface_language: i18n.language?.startsWith("ru") ? "ru" : "en" })
      .then(async ({ data }) => {
        const me = await signIn(data.token);
        const next = sessionStorage.getItem(NEXT_KEY);
        sessionStorage.removeItem(NEXT_KEY);
        navigate(me?.user?.role_choice && next ? next : next?.startsWith("/invite/") ? next : landingFor(me), { replace: true });
      })
      .catch(() => navigate("/login?google=failed", { replace: true }));
  }, [navigate, signIn]);
  return <Loading />;
}

function RequireAuth({ children, bare }) {
  const { me } = useAuth();
  const loc = useLocation();
  if (me === undefined) return <Loading />;
  if (!me) {
    sessionStorage.setItem(NEXT_KEY, loc.pathname + loc.search);
    return <Navigate to="/login" replace />;
  }
  if (!me.user.role_choice && !bare) return <Navigate to="/onboarding" replace />;
  if (!bare && me.user.active_role === "owner" && !/^\/(owner|profile|demo)/.test(loc.pathname)) return <Navigate to="/owner" replace />;
  return bare ? children : <Layout>{children}</Layout>;
}

function Root() {
  const { me } = useAuth();
  if (me === undefined) return <Loading />;
  return me ? <Navigate to={landingFor(me)} replace /> : <Landing />;
}

function AppRoutes() {
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  const P = (el) => <RequireAuth>{el}</RequireAuth>;
  return (
    <Routes>
      <Route path="/" element={<Root />} />
      <Route path="/login" element={<SignIn />} />
      <Route path="/register" element={<SignUp />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/invite/:token" element={<Invite />} />
      <Route path="/demo" element={<DemoStart />} />
      <Route path="/demo/modules" element={P(<DemoModules />)} />
      <Route path="/onboarding" element={<RequireAuth bare><Onboarding /></RequireAuth>} />
      <Route path="/profile" element={P(<Profile />)} />
      <Route path="/home" element={P(<Home />)} />
      <Route path="/practice" element={P(<Practice />)} />
      <Route path="/dogs/new" element={P(<DogNew />)} />
      <Route path="/dogs/:id" element={P(<DogDetail />)} />
      <Route path="/programmes/:id" element={P(<ProgrammeDetail />)} />
      <Route path="/visits/:id" element={P(<VisitPage />)} />
      <Route path="/tasks/:id" element={P(<TaskReview />)} />
      <Route path="/library" element={P(<Library />)} />
      <Route path="/library/:id" element={P(<ProtocolDetail />)} />
      <Route path="/workspace" element={P(<Workspace />)} />
      <Route path="/audit" element={P(<AuditLog />)} />
      <Route path="/owner" element={P(<OwnerHome />)} />
      <Route path="/owner/dogs/:id" element={P(<OwnerDog />)} />
      <Route path="/owner/tasks/:id" element={P(<OwnerTask />)} />
      <Route path="*" element={<Root />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
